// Audio conversion helpers — linear-interp resample + AudioData → interleaved
// Float32. Moved out of VideoAssetHandler.js (Phase C / Phase D Step 5):
// context-agnostic so the decode worker can run the exact same pipeline the
// main thread used through Phases 5–B.

// Worker-safe global lookup (window on the main thread, self in workers).
const g = (() => {
  if (typeof self !== 'undefined') return self
  if (typeof window !== 'undefined') return window
  return {}
})()

// Linear interpolation resampler with cross-chunk continuity.
// `state` is mutated in place: it tracks `srcOffset` (fractional source position
// carried over from the previous chunk) and `prevSample` (the last input sample
// of the previous chunk, used as input[-1] for the first interpolation). Pass a
// fresh empty object for the first call and reuse it for subsequent chunks of
// the same source channel to avoid per-chunk-boundary clicks.
export function linearResample(input, srcRate, dstRate, state) {
  if (!input || !input.length) return new Float32Array(0)
  if (!Number.isFinite(srcRate) || !Number.isFinite(dstRate) || srcRate <= 0 || dstRate <= 0) {
    return new Float32Array(0)
  }
  const ratio = srcRate / dstRate
  const inputLen = input.length

  const s = state || { srcOffset: 0, prevSample: 0 }
  if (!Number.isFinite(s.srcOffset)) s.srcOffset = 0
  if (!Number.isFinite(s.prevSample)) s.prevSample = 0

  // Number of output frames we can produce while keeping input[floor+1] in range.
  // Using ceil((inputLen - 1 - srcOffset) / ratio) guarantees the last srcPos
  // is < inputLen - 1 (so floor+1 <= inputLen - 1). The unused tail sample is
  // picked up next chunk via state carryover.
  const maxOutFrames = Math.max(0, Math.ceil((inputLen - 1 - s.srcOffset) / ratio))
  const output = new Float32Array(maxOutFrames)

  for (let i = 0; i < maxOutFrames; i++) {
    const srcPos = s.srcOffset + i * ratio
    const floor = Math.floor(srcPos)
    const frac = srcPos - floor
    const a = floor < 0 ? s.prevSample : input[floor]
    const b = input[floor + 1]
    output[i] = a + (b - a) * frac
  }

  s.srcOffset = s.srcOffset + maxOutFrames * ratio - inputLen
  s.prevSample = input[inputLen - 1]

  return output
}

// Phase D Step 1: resample a FULL interleaved buffer in one pass (linear
// interp). Used as the finalize-time fallback when OfflineAudioContext is
// unavailable. Returns the input untouched when no resample is needed.
export function resampleInterleaved(input, channels, srcRate, dstRate) {
  if (!input || !input.length) return input
  if (!Number.isFinite(srcRate) || !Number.isFinite(dstRate) || srcRate <= 0 || dstRate <= 0) {
    return input
  }
  if (Math.abs(srcRate - dstRate) <= 1) return input
  const ch = Math.max(1, channels | 0)
  const frames = Math.floor(input.length / ch)
  const planes = []
  for (let c = 0; c < ch; c++) {
    const plane = new Float32Array(frames)
    for (let f = 0; f < frames; f++) plane[f] = input[f * ch + c]
    planes.push(linearResample(plane, srcRate, dstRate, { srcOffset: 0, prevSample: 0 }))
  }
  let outFrames = planes[0]?.length || 0
  for (let c = 1; c < ch; c++) {
    if (planes[c].length < outFrames) outFrames = planes[c].length
  }
  const out = new Float32Array(outFrames * ch)
  for (let f = 0; f < outFrames; f++) {
    for (let c = 0; c < ch; c++) out[f * ch + c] = planes[c][f]
  }
  return out
}

// Helper: convert AudioData to interleaved Float32Array for storage.
//
// Args:
//   audioData         WebCodecs AudioData (planar Float32 preferred)
//   targetChannels    Output channel count (default 2)
//   targetSampleRate  Output sample rate. When provided and != source rate, the
//                     extracted planar samples are resampled with linear
//                     interpolation (Phase 5 — Option B). Pass null/undefined to
//                     keep the source's native rate.
//   resampleState     Optional `{ perChannel: [{srcOffset,prevSample}, ...] }`
//                     reused across chunks of the same source for click-free
//                     boundary stitching. Lazy-initialized on first call.
export const audioDataToInterleaved = (
  audioData,
  targetChannels = 2,
  targetSampleRate = null,
  resampleState = null
) => {
  const channelsIn = Math.max(1, audioData.numberOfChannels || 1)
  const chOut = targetChannels

  const sourceRate = Number(audioData.sampleRate)
  const wantRate = Number(targetSampleRate)
  // wantRate > 0 guard: null/undefined targetSampleRate means "keep native
  // rate" (the Phase D decode paths), but Number(null) is 0 — without the
  // guard that read as "resample to 0 Hz" and produced empty output.
  const needsResample =
    Number.isFinite(wantRate) && wantRate > 0 &&
    Number.isFinite(sourceRate) && Math.abs(sourceRate - wantRate) > 1

  // Extract planar data from AudioData at native sample rate.
  // Note: WebCodecs `copyTo` does NOT support resampling via opts.sampleRate
  // (the spec only defines planeIndex/frameOffset/frameCount/format), so any
  // rate conversion must be done in JS afterward.
  const planar = []
  let framesIn = null
  for (let c = 0; c < Math.min(channelsIn, chOut); c++) {
    const baseOpts = { planeIndex: c, format: 'f32-planar' }
    let arr = null
    try {
      const allocBytes = typeof audioData.allocationSize === 'function'
        ? audioData.allocationSize(baseOpts)
        : audioData.numberOfFrames * Float32Array.BYTES_PER_ELEMENT
      arr = new Float32Array(Math.max(1, Math.floor(allocBytes / Float32Array.BYTES_PER_ELEMENT)))
      audioData.copyTo(arr, baseOpts)
    } catch (errPlanar) {
      try {
        const nativeOpts = { planeIndex: c }
        const allocBytes = typeof audioData.allocationSize === 'function'
          ? audioData.allocationSize(nativeOpts)
          : audioData.numberOfFrames * Float32Array.BYTES_PER_ELEMENT
        arr = new Float32Array(Math.max(1, Math.floor(allocBytes / Float32Array.BYTES_PER_ELEMENT)))
        audioData.copyTo(arr, nativeOpts)
      } catch (errNative) {
        try {
          const f32Opts = { planeIndex: c, format: 'f32' }
          const allocBytes = typeof audioData.allocationSize === 'function'
            ? audioData.allocationSize(f32Opts)
            : audioData.numberOfFrames * Float32Array.BYTES_PER_ELEMENT
          arr = new Float32Array(Math.max(1, Math.floor(allocBytes / Float32Array.BYTES_PER_ELEMENT)))
          audioData.copyTo(arr, f32Opts)
        } catch (errF32) {
          return null // Failed to extract audio
        }
      }
    }
    if (framesIn == null || arr.length < framesIn) framesIn = arr.length
    planar.push(arr)
  }

  if (!planar.length || framesIn == null) return null

  // Resample each plane to targetSampleRate before downstream interleave/dup.
  // State (prevSample + srcOffset per channel) carries across chunks so that
  // adjacent decoded chunks stitch without boundary clicks.
  let framesOut = framesIn
  if (needsResample) {
    if (resampleState && !Array.isArray(resampleState.perChannel)) {
      resampleState.perChannel = []
    }
    for (let c = 0; c < planar.length; c++) {
      const trimmed = planar[c].length === framesIn ? planar[c] : planar[c].subarray(0, framesIn)
      let channelState
      if (resampleState) {
        if (!resampleState.perChannel[c]) resampleState.perChannel[c] = {}
        channelState = resampleState.perChannel[c]
      } else {
        channelState = {}
      }
      planar[c] = linearResample(trimmed, sourceRate, wantRate, channelState)
    }
    framesOut = planar[0]?.length || 0
    for (let c = 1; c < planar.length; c++) {
      if (planar[c].length < framesOut) framesOut = planar[c].length
    }
  }

  if (g.vybyTimingDebug) {
    try {
      g.__vybyDecodeRateLog = g.__vybyDecodeRateLog || new Set()
      const key = `${sourceRate}->${wantRate > 0 ? wantRate : 'native'}`
      if (!g.__vybyDecodeRateLog.has(key)) {
        g.__vybyDecodeRateLog.add(key)
        // eslint-disable-next-line no-console
        console.info('[audioDataToInterleaved] decode rate', {
          from: sourceRate || null,
          to: wantRate > 0 ? wantRate : null,
          resampled: needsResample,
          inFrames: framesIn,
          outFrames: framesOut,
          ratio: needsResample ? framesOut / framesIn : 1,
        })
      }
    } catch (e) { /* noop */ }
  }

  // Duplicate mono to stereo or fill missing channels with silence (operates
  // on resampled planes when needsResample, otherwise on native planes).
  if (channelsIn === 1 && chOut > 1) {
    const mono = planar[0]
    for (let extra = planar.length; extra < chOut; extra++) {
      const dup = new Float32Array(framesOut)
      dup.set(mono.subarray(0, framesOut))
      planar.push(dup)
    }
  } else if (channelsIn < chOut) {
    for (let extra = planar.length; extra < chOut; extra++) {
      planar.push(new Float32Array(framesOut))
    }
  }

  // Interleave
  const interleaved = new Float32Array(framesOut * chOut)
  for (let i = 0; i < framesOut; i++) {
    for (let c = 0; c < chOut; c++) {
      const plane = planar[c]
      interleaved[i * chOut + c] = plane ? plane[i] : 0
    }
  }

  return interleaved
}
